import { createNotifications } from '~/shared/utils/create-product-notifications';
import { Product } from '~/lib/data-contract/salesforce';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { toBoolean } from '~/shared/utils/toBoolean';
import { TranslateFn } from '~/shared/utils/translation/hooks/useTranslation';
import { truncateText } from '~/shared/utils/truncate-text';

const getCTAButtonProductCard = (product: Product) => {
    const { primaryButton: primary, secondaryButton: secondary } = product.ctaButtons || {};

    if (primary && !toBoolean(primary.isDisabled) && toBoolean(primary.shouldRender)) {
        return primary;
    } else if (secondary && !toBoolean(secondary.isDisabled) && toBoolean(secondary.shouldRender)) {
        return secondary;
    }

    return null; // Return null if no buttons meet the criteria
};

const getPotentialBonusEarning = (
    product: Product,
    translate: TranslateFn,
    convertPriceToLocale: (price: number) => string,
) => {
    const bonusEarning = parseFloat(product.bonus || '0');
    if (bonusEarning) {
        return translate('pdp.bonusEarning', {
            amount: convertPriceToLocale(product.bonus),
        });
    }
    return 0; // Return 0 if there is no bonus or it's not parseable
};

export const mapRaptorProductData = (
    product: Product,
    translate: TranslateFn,
    convertPriceToLocale: (price: number) => string,
): TProductDetails => {
    const ctaButtonProductCard = getCTAButtonProductCard(product);

    const notifications = createNotifications(
        {
            ...product,
            c_minimumShippingCost: product?.minimumShippingCost,
            c_persuasiveText: product?.persuasiveText,
            c_prices: product?.prices?.priceList,
        },
        translate,
        convertPriceToLocale,
    );

    return {
        name: truncateText(product?.productName ?? '', 26),
        longName: product.productName,
        potentialBonusEarning:
            getPotentialBonusEarning(product, translate, convertPriceToLocale) || undefined,
        splash: product.splashText ? { text: product.splashText, variant: 'campaign' } : undefined,
        brand: product.brandName, // Assuming 'brand' is always present
        cust_synopsis: product.synopsis ?? null,
        cust_isNew: toBoolean(product.isNew ?? 'false'),
        cust_in_stock: toBoolean(product.inStock ?? 'false'),
        //@ts-ignore
        c_prices: product?.prices.priceList,
        id: product.productId ?? null,
        ctaButtons: {
            primary: product?.ctaButtons?.primaryButton,
            secondary: product?.ctaButtons?.secondaryButton,
        },
        images: [product.images],
        url: product.productUrl,
        label: product.synopsis,
        cust_promotionId: product.promotionId,
        ctaButtonProductCard,
        infoTopRight: toBoolean(product.soleRepresentation ?? 'false')
            ? translate('pcard.soleRepresentation')
            : undefined,
        cust_sole_representation: toBoolean(product.soleRepresentation ?? 'false'),
        cust_yotpoAverageRating: parseFloat(product.yotpoAverageRating ?? '0'),
        cust_yotpoReviewCount: parseInt(product.yotpoReviewCount ?? '0', 0),
        notifications,
        cust_specialPrices: product.specialPrice,
        cust_swatchimage: product.swatchImage,
        cust_swatchimages_siblings: product.swatchImageSiblings,
        cust_hasSiblings: product.hasSiblings,
    } as TProductDetails;
};

export const isValidId = (value: unknown): value is string => typeof value === 'string' && !!value;
